import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { ProductCard, ProductCardSkeleton } from "@/components/ProductCard";
import { Store, Star, Package, Phone, Loader2, MapPin } from "lucide-react";

export default function SellerStorefront() {
  const { sellerId } = useParams();
  const { t } = useLang();
  const [shop, setShop] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setShop(null);
    window.scrollTo(0, 0);
    api.get(`/sellers/${sellerId}`).then((r) => setShop(r.data)).catch(() => setNotFound(true));
  }, [sellerId]);

  if (notFound) return <div className="min-h-[50vh] grid place-items-center text-stone-400" data-testid="shop-not-found">{t("shop_not_found")}</div>;
  if (!shop) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-800" /></div>;

  return (
    <div data-testid="seller-storefront">
      {/* Shop header */}
      <div className="relative text-white grain overflow-hidden" data-testid="shop-cover">
        {shop.shop_cover
          ? <><img src={mediaUrl(shop.shop_cover)} alt="" className="absolute inset-0 w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-emerald-950/95 via-emerald-900/80 to-emerald-800/55" /></>
          : <div className="absolute inset-0 bg-emerald-800" />}
        <div className="relative max-w-7xl mx-auto px-6 py-14 flex flex-col sm:flex-row items-center sm:items-end gap-6">
          <div className="w-28 h-28 rounded-2xl bg-white/10 border-4 border-white/20 grid place-items-center overflow-hidden shrink-0">
            {shop.shop_logo ? <img src={mediaUrl(shop.shop_logo)} alt={shop.shop_name} className="w-full h-full object-cover" data-testid="shop-logo" />
              : <Store className="w-12 h-12 text-mango-400" />}
          </div>
          <div className="text-center sm:text-left flex-1">
            <span className="text-mango-400 text-xs font-bold uppercase tracking-widest">{t("shop_by")}</span>
            <h1 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight mt-1" data-testid="shop-name">{shop.shop_name}</h1>
            {shop.shop_bio && <p className="text-emerald-100 mt-2 max-w-2xl leading-relaxed">{shop.shop_bio}</p>}
            <div className="flex items-center justify-center sm:justify-start gap-5 mt-4 text-sm text-emerald-100">
              <span className="flex items-center gap-1"><Package className="w-4 h-4 text-mango-400" /> {shop.product_count} {t("nav_catalog").toLowerCase()}</span>
              {shop.rating > 0 && <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-mango-400 text-mango-400" /> {shop.rating}</span>}
              {shop.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4 text-mango-400" /> {shop.phone}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-2 text-sm text-stone-500 mb-6"><MapPin className="w-4 h-4" /> Marché Assiganme, Lomé</div>
        {shop.products.length === 0 ? (
          <div className="py-20 text-center text-stone-400">{t("no_results")}</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {shop.products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
